import {
  Box,
  CircularProgress,
} from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import minMax from 'dayjs/plugin/minMax';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import BaseHighchart from '../../BaseHighchart';
import { ChartDataType } from '../../types';
import { ReduxState } from '../../../../types';
import { navigateToChart } from '../../../../utils/helpers';
import ChartFilter from '../../ChartFilter';
import { getChartConfig, getYAxis, calculateYAxisLimits } from '../utils';
import { COLD_HOURS_NAME, CHART_TITLE_HORAS_FRIO_ACUMULADAS } from '../../../../constants/graphs';
import apiClient from '../../../../requests/api/apiClient';
import { ColdHoursChartProps } from './types';
import { getPlotBands } from '../../utils';
import ChartConfigDialog from '../../ChartConfigDialog/ChartConfigDialog';
import ColdHoursConfigContent from './ColdHoursConfigContent';
import useChartConfigClick from '../../../../hooks/useChartConfigClick';

dayjs.extend(utc);
dayjs.extend(minMax);

function ColdHoursChart({
  chartView,
  sectorId,
  farmId,
  hideLegend = false,
  hideXValues = false,
  hideYValues = false,
  hideRightYAxisTitle = false,
  height = 600,
  title = CHART_TITLE_HORAS_FRIO_ACUMULADAS,
  yAxisTitle = 'Horas Frío Acumuladas',
  refreshKey,
  isWeatherStation,
}: ColdHoursChartProps) {
  const chartName = 'horas-frio';

  const [chartData, setChartData] = useState<ChartDataType>({
    series: [],
    categories: [],
    absoluteCategories: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const history = useHistory();

  const { interval } = useSelector((state: ReduxState) => state.graphsFilter);
  const selectedSector = useSelector((state: ReduxState) => state.currentSector.selectedSector);

  const fetchConfigAndData = useCallback(async () => {
    setIsLoading(true);
    try {
      if (interval) {
        const { data } = await apiClient.readings.getColdHoursChartData({
          farmId,
          startDate: interval.startDate,
          endDate: interval.endDate,
          sectorId,
          telemetryType: selectedSector?.telemetryType,
          sn: selectedSector?.telemetryType === 'sn' ? selectedSector.weatherZoneId : undefined,
          chart: COLD_HOURS_NAME,
          isWeatherStation,
        });

        setChartData({
          series: data.series,
          absoluteCategories: data.absoluteCategories,
        });
      }
    } catch (error) {
      console.error('Error fetching config/data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [farmId, sectorId, selectedSector, interval, isWeatherStation]);

  useEffect(() => {
    fetchConfigAndData();
  }, [fetchConfigAndData]);

  // Re-fetch when the settings page saves a new config (refreshKey increments).
  useEffect(() => {
    if (refreshKey !== undefined && refreshKey > 0) {
      fetchConfigAndData();
    }
  }, [refreshKey]);

  const chartConfig = useMemo(
    () => {
      if (!interval) {
        return undefined;
      }
      const yAxisLimits = calculateYAxisLimits(chartData.series);

      const firstDay = dayjs(interval.startDate).utc().startOf('day').valueOf();
      const lastDay = dayjs(dayjs(interval.endDate).valueOf()).add(dayjs().utcOffset(), 'minutes').valueOf();

      const yAxisConfig: Highcharts.YAxisOptions[] = [
        {
          ...getYAxis('Horas frío acumuladas', hideYValues),
          min: yAxisLimits.min,
          max: yAxisLimits.max,
          id: 'temperature',
        } as Highcharts.YAxisOptions,
        {
          title: { text: hideRightYAxisTitle ? '' : 'Días grado acumulados' },
          min: yAxisLimits.min,
          max: yAxisLimits.max,
          id: 'grados-acumulador',
          opposite: true,
          labels: { enabled: !hideYValues && !hideRightYAxisTitle },
        } as Highcharts.YAxisOptions,
      ];

      return ({
        ...getChartConfig(
          chartData,
          height,
          hideXValues,
          undefined,
          dayjs(interval.startDate).valueOf(),
          dayjs(interval.endDate).valueOf(),
        ),
        yAxis: yAxisConfig,
        plotOptions: {
          series: {
            marker: {
              enabled: true,
            },
          },
        },
        xAxis: {
          type: 'datetime',
          min: firstDay,
          max: lastDay,
          labels: {
            format: '{value:%b %d}',
            enabled: !hideXValues,
          },
          plotLines: (() => {
            const lines: any[] = [];
            let currentDay = '';

            if (chartData.absoluteCategories) {
              chartData.absoluteCategories.forEach((dateStr: string, index: number) => {
                const date = dayjs(dateStr).utc().startOf('day');
                const day = date.format('YYYY-MM-DD');

                if (day !== currentDay && index > 0) {
                  lines.push({
                    color: '#999999',
                    width: 1,
                    dashStyle: 'Dash',
                    value: date.valueOf(),
                  });
                  currentDay = day;
                }
              });
            }
            return lines;
          })(),
          plotBands: getPlotBands(chartData.absoluteCategories),
        },
      });
    },
    [
      height,
      hideXValues,
      chartData.absoluteCategories,
      hideYValues,
      hideRightYAxisTitle,
      interval,
      chartData.series,
    ],
  );

  const seriesWithAxis = useMemo(
    () => {
      const series = (chartData.series as Highcharts.SeriesOptionsType[]).map(
        (serie: any) => {
          if (serie.name === 'Días grado acumulados') {
            return { ...serie, yAxis: 'grados-acumulador' };
          }
          return serie;
        },
      );

      return series;
    },
    [chartData.series],
  );

  const handleConfigClick = useChartConfigClick({
    chartView,
    route: chartName,
    farmId,
    sectorId,
    onFullScreen: () => setConfigDialogOpen(true),
  });

  const handleFullScreenClick = () => {
    navigateToChart(`fullScreen/${chartName}`, history, {
      farmId,
      sectorId,
    });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={height}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <div>
        <BaseHighchart
          chartView={chartView}
          sectorId={sectorId}
          filter={chartView === 'settings' ? <ChartFilter sectorId={sectorId} /> : undefined}
          title={title}
          yAxisTitle={yAxisTitle}
          series={seriesWithAxis}
          hideYValues={hideYValues}
          showFullScreen={true}
          legend={{ enabled: !hideLegend }}
          chartConfig={chartConfig}
          onConfigClick={handleConfigClick}
          onFullScreenClick={handleFullScreenClick}
          absoluteCategories={chartData.absoluteCategories}
          height={height}
          zoomSync={true}
          autoAdjustYAxis={true}
          interval={interval}
          graphName={COLD_HOURS_NAME}
        />
      </div>
      <ChartConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        title="Configuración · Horas frío"
      >
        <ColdHoursConfigContent
          sectorId={sectorId}
          onConfigSaved={() => fetchConfigAndData()}
        />
      </ChartConfigDialog>
    </>
  );
}

export default ColdHoursChart;
